import type { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import { Box, Checkbox, IconButton, Tooltip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	TEXT_CELL_CLASS,
} from "@/controls/ag-grid/OneLineCells";
import type { Recipe } from "../models/Recipe";
import { activeColumnDef } from "@/controls/ag-grid/components/ActiveColumn";

interface UseTableDataProps {
	onDelete: (recipe: Recipe) => void;
}

export const useTableData = ({ onDelete }: UseTableDataProps) => {
	const columnDefs: ColDef<Recipe>[] = [
		{
			field: "Name",
			headerName: "Name",
			flex: 1.2,
			minWidth: 100,
			sortable: true,
			filter: true,
			cellClass: [ONE_LINE_CELL_CLASS, TEXT_CELL_CLASS],
		},
		{
			field: "ProductTypeName",
			headerName: "Product Type",
			flex: 1,
			minWidth: 90,
			sortable: true,
			filter: true,
			cellClass: [ONE_LINE_CELL_CLASS, TEXT_CELL_CLASS],
		},
		{
			field: "ProductSizeName",
			headerName: "Product",
			flex: 1.4,
			minWidth: 120,
			sortable: true,
			filter: true,
			cellClass: [ONE_LINE_CELL_CLASS, TEXT_CELL_CLASS],
		},
		{
			field: "LineSpeed",
			headerName: "Line Speed",
			flex: 0.7,
			minWidth: 70,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "StartTemperature",
			headerName: "Start Temp",
			flex: 0.7,
			minWidth: 70,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "EndTemperature",
			headerName: "End Temp",
			flex: 0.7,
			minWidth: 70,
			cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
			valueFormatter: (params: { value: number | null }) =>
				params.value != null ? String(params.value) : "",
		},
		{
			field: "EnableComparison",
			headerName: "Compare",
			flex: 0.5,
			sortable: false,
			filter: false,
			cellRenderer: (params: ICellRendererParams<Recipe>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Checkbox
							checked={params.data.EnableComparison}
							size="small"
							disabled
							sx={{ p: 0 }}
						/>
					</Box>
				);
			},
		},
		activeColumnDef<Recipe>(),
		{
			headerName: "",
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			filter: false,
			resizable: false,
			suppressSizeToFit: true,
			cellRenderer: (params: ICellRendererParams<Recipe>) => {
				if (!params.data) return null;
				return (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							height: "100%",
						}}
					>
						<Tooltip title="Delete">
							<IconButton
								size="small"
								onClick={(e) => {
									e.stopPropagation();
									onDelete(params.data as Recipe);
								}}
								color="error"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
				);
			},
		},
	];

	return { columnDefs };
};
